import type { ApiError } from './useError';
import type { User } from '@/types/user/type';
import { useLocale } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { isAuthenticated as checkIsAuthenticated, fetchItem, getLoginUri, revokeAuthenticationTokens } from '@/app/actions/server';
import { useApiErrorContext } from '@/app/components/Provider';
import { dataLayerPush } from '@/app/utils/analytics/datalayer';
import { getErrorCodeFromUri } from '@/app/utils/request/response';
import { defaultLocale, usePathname, useRouter } from '@/i18n/routing';
import { AnalyticEvents, CookieName } from '~/const/appConst';
import { AcceptLanguage, LocaleCode } from '~/const/locale';
import { getSessionStorage, setCookie } from '~/utils/utils';
import { isGuestUser } from '../utils/entities/user';

export function useAuthentication() {
  const locale = useLocale();
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();

  const { error, setError } = useApiErrorContext();

  const [user, setUser] = useState<Partial<User>>();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>();
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  const revokeAuthentication = async () => {
    await revokeAuthenticationTokens();
    setUser({});
    setIsAuthenticated(false);
  };

  const initUser = useCallback(async () => {
    const { authenticated, asGuest } = await checkIsAuthenticated();

    if (!authenticated) {
      return revokeAuthentication();
    }

    setIsAuthenticating(true);

    const response = await fetchItem<User & ApiError>({
      pathname: asGuest ? 'guest_users/me' : 'users/me',
      revalidate: 0,
    });

    if (response?.message || response?.['hydra:description']) {
      await revokeAuthentication();
      setIsAuthenticating(false);
      return setError(response);
    }

    setUser(response);

    if (response.favorite_locale !== AcceptLanguage[locale]) {
      setCookie(`${CookieName.COCREATION_LOCALE}=${response.favorite_locale}`);
      setCookie(`${CookieName.NEXT_LOCALE}=${LocaleCode[response.favorite_locale]}`);
      router.replace(pathname, {
        locale: LocaleCode[response.favorite_locale],
      });
    }

    if (asGuest) {
      router.replace(pathname);
    } else {
      dataLayerPush({
        event: AnalyticEvents.CONNECTED,
        page_name: pathname,
      });
    }

    setIsAuthenticating(false);
    setIsAuthenticated(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const redirectToLogin = async (href?: string) => {
    const isLucky = getSessionStorage('isLucky');

    const newSearchParams = new URLSearchParams(searchParams);

    if (isLucky) {
      newSearchParams.append('luckyMember', 'true');
    }

    const search = newSearchParams.toString();
    const loginUriSearchParams = search ? `?${search}` : '';
    await getLoginUri(href || `${pathname}${loginUriSearchParams}`);
  };

  useEffect(() => {
    if (pathname) {
      const localeUrl = locale === defaultLocale ? '' : `/${locale}`;

      dataLayerPush({
        page_type: pathname,
        page_name: `${localeUrl}${pathname}`,
        page_language: AcceptLanguage[locale],
        user_id: user?.id?.toString(),
        user_login_status: !!user?.id,
        user_sport: user?.sports?.map(sport => sport.label).toString(),
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, searchParams, user?.id, locale]);

  useEffect(() => {
    if (isGuestUser(user)) {
      return;
    }

    if (user?.id && user?.term_of_use_accepted === false) {
      dataLayerPush({
        event: AnalyticEvents.SIGN_UP,
        user_id: user?.id?.toString(),
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    initUser();
  }, [initUser]);

  useEffect(() => {
    if (
      error
      && getErrorCodeFromUri(error['@id']) === 401
      && error['hydra:description'] === 'Token signature mismatch'
    ) {
      revokeAuthentication();
    }
  }, [error]);

  return {
    user,
    isAuthenticated,
    isAuthenticating,
    redirectToLogin,
    revokeAuthentication,
    setUser,
    setIsAuthenticated,
  };
}
